import { useEffect, useState } from 'react';
import { combineLatest, from } from 'rxjs';
import { distinctBy } from '../../utility';
import { useTranslation } from 'react-i18next';
import {
  ITranslationDetailProps,
  PriceRender,
  StyledDialog,
  TranslationDetail,
} from '../../ui';
import { IProductApi, ProductApi } from '../../services/productApi';
import { IProductDto } from '../../model/IProductDto';
import { IOfferProductDto } from '../../model/IOfferProductDto';

export interface IOfferProductDisplayProps {
  offerProducts: IOfferProductDto[];
}

export function OfferProductDisplay(props: IOfferProductDisplayProps) {
  const { t, i18n } = useTranslation('offer-package');
  const api: IProductApi = new ProductApi();
  const { offerProducts } = props;

  const [products, setProducts] = useState<IProductDto[]>();
  const [modalContent, setModalContent] = useState<ITranslationDetailProps>();

  useEffect(() => {
    const idsToLoad = [...distinctBy(offerProducts, (e) => e.productId)]
      .map((e) => e.productId)
      .map((e) => from(api.getById(e)));

    combineLatest(idsToLoad).subscribe({
      next: (data) => setProducts(data),
    });
  }, [offerProducts]);

  if (!products) {
    return <p>Loading...</p>;
  }

  const renderProduct = (id: number) => {
    const product = products?.find((e) => e.id === id);
    if (!product) {
      return '';
    }

    const translation = product?.productTranslationList.find(
      (e) => e.langCode === i18n.language
    );

    return translation?.title;
  };

  return (
    <div>
      <StyledDialog
        isOpen={modalContent !== undefined}
        onClose={() => setModalContent(undefined)}
      >
        {modalContent && (
          <TranslationDetail
            text={modalContent.text}
            imageLinks={modalContent.imageLinks}
          ></TranslationDetail>
        )}
      </StyledDialog>

      <h3 className="text-lg bg-brand-green-700 text-white -mx-4 px-4 py-2">
        {t('product.title')}
      </h3>

      <ul className="-mx-4 divide-y">
        {offerProducts.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className="flex group justify-between items-center w-full gap-2 px-6 hover:bg-slate-100 py-2 print:p-0"
              onClick={() => {
                const product = products?.find((e) => e.id === item.productId);
                const translation = product?.productTranslationList.find(
                  (e) => e.langCode === i18n.language
                );

                if (translation) {
                  setModalContent({
                    text: translation,
                    imageLinks: product?.imageLinks ?? [],
                  });
                }
              }}
            >
              <div className="text-left">
                <p className="font-bold">
                  {item.amount}x {renderProduct(item.productId)}
                </p>
                <p className="group-hover:underline mt-2">
                  <strong>{t('shared.more')}...</strong>
                </p>
              </div>

              <div className="text-right">
                <PriceRender
                  price={item.price}
                  offerPrice={item.offerPrice}
                ></PriceRender>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default OfferProductDisplay;
